import { useEffect, useRef } from 'react';

import {
  type DocyrusCalendarEventPayload,
  type DocyrusEmailPayload,
  type DocyrusSharePayload,
  type GuidyRoute,
  type HostNavigationHandler,
  type HostNavigationRequestOptions,
  type HostNotificationHandler
} from '../types';

import { type MsGraphClient } from '../core/ms-graph-client';

import { useDocyrusAuth } from './use-docyrus-auth';

/**
 * Auto-sync the embedded app's route to the host on every history change.
 * Equivalent to setting `syncRouteToHost: true` on the provider, but
 * scoped to wherever this hook is mounted (typically the app shell).
 * Safe to call once per app. No-op outside iframe mode.
 */
export function useDocyrusHostRouteSync(): void {
  const { enableHostRouteSync } = useDocyrusAuth();

  useEffect(() => {
    enableHostRouteSync();
  }, [enableHostRouteSync]);
}

/**
 * Subscribe to host `navigation` messages from within an embedded iframe/WebView.
 * The latest handler closure is always invoked — no need to memoize.
 * No-op in non-iframe modes.
 */
export function useDocyrusHostNavigation(handler: HostNavigationHandler): void {
  const { onHostNavigation } = useDocyrusAuth();
  const handlerRef = useRef(handler);

  handlerRef.current = handler;

  useEffect(() => {
    return onHostNavigation((message) => {
      handlerRef.current(message);
    });
  }, [onHostNavigation]);
}

/**
 * Subscribe to host `notification` messages from within an embedded iframe/WebView.
 * The latest handler closure is always invoked — no need to memoize.
 * No-op in non-iframe modes.
 */
export function useDocyrusHostNotification(handler: HostNotificationHandler): void {
  const { onHostNotification } = useDocyrusAuth();
  const handlerRef = useRef(handler);

  handlerRef.current = handler;

  useEffect(() => {
    return onHostNotification((notification) => {
      handlerRef.current(notification);
    });
  }, [onHostNotification]);
}

/**
 * Get a stable function that asks the host shell to navigate to a path/URL
 * from within an embedded iframe/WebView. The reverse of
 * `useDocyrusHostNavigation`. No-op in non-iframe modes.
 */
export function useDocyrusRequestHostNavigation(): (
  url: string,
  options?: HostNavigationRequestOptions
) => void {
  const { requestHostNavigation } = useDocyrusAuth();

  return requestHostNavigation;
}

/**
 * Declare the routes the host assistant (Guidy) may navigate the user to
 * inside this embedded app. Re-syncs whenever `routes` changes. Requires
 * `enableGuidyBridge` on the provider; no-op in non-iframe modes.
 */
export function useDocyrusGuidyRoutes(routes: GuidyRoute[]): void {
  const { setGuidyRoutes } = useDocyrusAuth();

  useEffect(() => {
    setGuidyRoutes(routes);
  }, [setGuidyRoutes, routes]);
}

/**
 * Get a stable function that hands a payload to the host AI assistant (Docy)
 * from within an embedded iframe/WebView. Resolves when the host acknowledges
 * delivery; rejects on host error or timeout (and immediately outside iframe
 * mode). Use for "Send to Docy" buttons/menu items.
 */
export function useDocyrusSendToDocy(): (payload: DocyrusSharePayload) => Promise<void> {
  const { sendToDocy } = useDocyrusAuth();

  return sendToDocy;
}

/**
 * Get a stable function that hands a payload to the host team-chat app from
 * within an embedded iframe/WebView. Resolves when the host acknowledges
 * delivery; rejects on host error or timeout (and immediately outside iframe
 * mode). Use for "Send to Chat" buttons/menu items.
 */
export function useDocyrusSendToChat(): (payload: DocyrusSharePayload) => Promise<void> {
  const { sendToChat } = useDocyrusAuth();

  return sendToChat;
}

/**
 * Get a stable function that hands a draft email (to/subject/body/cc) to the
 * host from within an embedded iframe/WebView. Resolves when the host
 * acknowledges; rejects on host error or timeout (and immediately outside
 * iframe mode). Use for "Send to Email" buttons/menu items.
 */
export function useDocyrusSendToEmail(): (payload: DocyrusEmailPayload) => Promise<void> {
  const { sendToEmail } = useDocyrusAuth();

  return sendToEmail;
}

/**
 * Get a stable function that hands a calendar event (subject/description/
 * participants/start/end) to the host from within an embedded iframe/WebView.
 * Resolves when the host acknowledges; rejects on host error or timeout (and
 * immediately outside iframe mode). Use for "Send to Calendar" buttons/menu
 * items.
 */
export function useDocyrusSendToCalendar(): (payload: DocyrusCalendarEventPayload) => Promise<void> {
  const { sendToCalendar } = useDocyrusAuth();

  return sendToCalendar;
}

/**
 * Get the client for the host's Microsoft Graph (MSAL) session. Check
 * `isSignedIn()` and make host-proxied Graph requests. Returns `null` outside
 * iframe/WebView mode.
 */
export function useDocyrusMsGraph(): MsGraphClient | null {
  const { msGraph } = useDocyrusAuth();

  return msGraph;
}
